/**
 * Every key the order form answers to, for the question mark in its header.
 *
 * On request rather than in the header all day: the keys are what lets a dispatcher take a
 * whole call without reaching for the mouse, and nobody finds Space on a requirement or Alt
 * and a digit on the address by accident. Written as keys and what each does, one to a line,
 * because a paragraph of shortcuts is read once and remembered never. A key named inside a
 * line's words is written `[Tab]` in the translation and drawn as a key too.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { EntranceWay } from './AddressField';

/** The lines that are always there, in the order a call is taken; the words are `order.keysHelp.<line>`. */
const BEFORE = [
  { keys: ['Tab'], line: 'tab' },
  { keys: ['↑', '↓'], line: 'arrows' },
] as const;

const AFTER = [
  { keys: ['space'], line: 'space' },
  { keys: ['Enter'], line: 'enter' },
  { keys: ['Esc'], line: 'esc' },
] as const;

/** The letters the entrance line shows as its example: the firm writes its doors in Cyrillic whatever the reader's language. */
const EXAMPLE_DOORS = ['А', 'Б', 'В', 'Г'];

/** The keys that are words rather than characters, drawn in the reader's language: `order.keysHelp.<key>Key`. */
const WORDED = new Set(['space', 'letter']);

/** One key, drawn as the key it is. */
function Key({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-block min-w-[1.75em] px-1.5 rounded-control border border-rule bg-surface-2 text-ink font-sans text-[11px] font-semibold leading-[18px] text-center">
      {children}
    </kbd>
  );
}

/** A line's words, with every `[Key]` in them drawn as a key. */
function withKeys(text: string): ReactNode[] {
  return text.split(/\[([^\]]+)\]/).map((part, at) => (at % 2 === 1 ? <Key key={at}>{part}</Key> : part));
}

/**
 * @param scopes The address switch's buttons as this firm has them, left to right — Alt and
 *   a digit picks one by its place, so the digits are only right when they are read from the
 *   same list the switch is drawn from. A firm that drew no nearby area has three, and its
 *   Auto is 3, not 4. Empty when there is no switch, and then there is no line either.
 * @param entrances How this dispatcher's box offers a building's entrances. Only *after the
 *   address* waits for a letter once Enter has taken the building — in the list each door is
 *   a row the arrows already reach — so only then is there a line for it.
 */
export function OrderKeys({ scopes, entrances }: { scopes: readonly string[]; entrances: EntranceWay }) {
  const { t } = useTranslation();

  const lines: { keys: readonly string[]; text: string; then?: ReactNode }[] = [
    ...BEFORE.map(({ keys, line }) => ({ keys, text: t(`order.keysHelp.${line}`) })),
    ...(entrances === 'after'
      ? [{
          keys: ['letter'],
          text: t('order.keysHelp.letter'),
          // An example of the letters as they appear under the box, each with its door, then
          // the two ways out: what happens to a building nobody picked a door for is the
          // question a dispatcher comes back to the help with.
          then: (
            <span className="flex flex-col gap-1 mt-1">
              <span className="flex flex-wrap gap-x-2.5 gap-y-1">
                {EXAMPLE_DOORS.map((letter) => (
                  <span key={letter} className="inline-flex items-baseline gap-1 whitespace-nowrap">
                    <Key>{letter}</Key>
                    {t('order.keysHelp.letterDoor', { letter })}
                  </span>
                ))}
              </span>
              <span>{withKeys(t('order.keysHelp.letterKeep'))}</span>
              <span>{withKeys(t('order.keysHelp.letterNew'))}</span>
            </span>
          ),
        }]
      : []),
    ...(scopes.length === 0
      ? []
      : [{
          keys: ['Alt', `1–${scopes.length}`],
          text: t('order.keysHelp.scope'),
          // Each digit with its button, kept together, so a line never breaks between them.
          then: (
            <span className="flex flex-wrap gap-x-2.5 gap-y-1 mt-1">
              {scopes.map((scope, at) => (
                <span key={scope} className="inline-flex items-baseline gap-1 whitespace-nowrap">
                  <Key>{at + 1}</Key>
                  {scope}
                </span>
              ))}
            </span>
          ),
        }]),
    ...AFTER.map(({ keys, line }) => ({ keys, text: t(`order.keysHelp.${line}`) })),
  ];

  return (
    <span className="flex flex-col gap-2">
      {/* The note's title: what all of it adds up to, before the keys that do it. */}
      <span className="pb-1.5 border-b border-rule text-body font-semibold text-ink">
        {t('order.keysHelp.intro')}
      </span>

      <span className="grid grid-cols-[auto_1fr] items-baseline gap-x-2.5 gap-y-1.5">
        {lines.map(({ keys, text, then }) => (
          <span key={keys.join()} className="contents">
            <span className="inline-flex gap-1 whitespace-nowrap">
              {keys.map((key) => (
                <Key key={key}>{WORDED.has(key) ? t(`order.keysHelp.${key}Key`) : key}</Key>
              ))}
            </span>
            <span>
              {withKeys(text)}
              {then}
            </span>
          </span>
        ))}
      </span>

      <span className="text-ink-2">{t('order.keysHelp.after')}</span>
    </span>
  );
}
